/**
 * Hook for managing drag-and-drop state with optimistic updates.
 *
 * Provides state management for @dnd-kit drag operations with:
 * - Optimistic UI updates (card moves immediately)
 * - Support for multiple concurrent in-flight moves
 * - Automatic rollback on API failure
 * - Toast notifications for errors
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { logger } from '@/lib/logger';

import { getUserFriendlyEventError } from '../../core/errors';
import type { DragState, UsePipelineDragDropReturn } from '../types';
import { useOptimisticOverrideCleanup } from './useOptimisticOverrideCleanup';

interface UsePipelineDragDropOptions<T> {
  /** Original items array */
  items: T[];
  /** Extract ID from an item */
  getItemId: (item: T) => string | number;
  /** Get current stage of an item */
  getItemStage: (item: T) => string;
  /** Create a new item with updated stage */
  setItemStage: (item: T, stage: string) => T;
  /** API call to persist the stage change */
  onStageChange: (item: T, newStage: string) => Promise<void>;
  /** Refresh data after successful update */
  onRefresh: () => void;
  /** Called when optimistic update is rolled back due to API failure */
  onRollback?: () => void;
  /**
   * The set of stage IDs the board currently renders. When provided,
   * optimistic overrides targeting a stage NOT in this set are ignored (the
   * item renders at its server stage) and proactively cleared. Without this,
   * dropping onto a stage that a teammate deleted mid-drag leaves an
   * override pointing at a column that no longer exists — the item lands in
   * no rendered group and vanishes from the board until the safety timeout
   * clears it.
   */
  validStageIds?: ReadonlySet<string>;
}

/**
 * Manages drag-and-drop state with optimistic updates and rollback.
 *
 * Supports multiple concurrent optimistic updates so that rapid successive
 * drags don't cause previously moved items to snap back to their original
 * position while their API call is still in flight.
 *
 * @example
 * ```tsx
 * const {
 *   dragState,
 *   optimisticItems,
 *   handleDragStart,
 *   handleDragOver,
 *   handleDragEnd,
 *   handleDragCancel,
 *   isUpdating,
 * } = usePipelineDragDrop({
 *   items: deals,
 *   getItemId: (deal) => deal.id,
 *   getItemStage: (deal) => deal.stage,
 *   setItemStage: (deal, stage) => ({ ...deal, stage }),
 *   onStageChange: async (deal, stage) => {
 *     await api.updateDealStage(deal.id, stage);
 *   },
 *   onRefresh: refetch,
 * });
 * ```
 */
export function usePipelineDragDrop<T>({
  items,
  getItemId,
  getItemStage,
  setItemStage,
  onStageChange,
  onRefresh,
  onRollback,
  validStageIds,
}: UsePipelineDragDropOptions<T>): UsePipelineDragDropReturn<T> {
  // Current drag state
  const [dragState, setDragState] = useState<DragState<T>>({
    activeItem: null,
    originalStage: null,
    overStage: null,
  });

  // Optimistic stage overrides (before API confirms).
  // Supports multiple concurrent moves so rapid drags don't cause snap-back.
  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Map<string | number, string>
  >(new Map());

  // Loading state during API call
  const [isUpdating, setIsUpdating] = useState(false);

  // Track pending clear timeouts per item to prevent race conditions
  const clearTimeoutsRef = useRef<
    Map<string | number, ReturnType<typeof setTimeout>>
  >(new Map());

  // Track active operations count for isUpdating state
  const activeOperationsRef = useRef(0);

  // Track in-flight API operations PER ITEM. A request's generation number may
  // only be retired once no in-flight request for that item still holds a
  // captured generation — otherwise resetting the counter lets a later move
  // reuse a generation value a stale in-flight request captured, defeating the
  // ownership guard (the move A→B, B→A, A→B race). Deletion sites below gate on
  // this being zero; the value also gates the safety timeout's own cleanup.
  const inFlightByItemRef = useRef<Map<string | number, number>>(new Map());

  // Track per-item request generation to identify timeout ownership.
  // Incremented each time a fresh optimistic override is set; the captured
  // local value lets the timeout-install guard and expiry callback verify
  // that no newer request has taken over the same item — necessary when two
  // different requests target the same stage (stage-equality alone cannot
  // distinguish them: move A→B, B→A, then A→B again leaves request 1 and
  // request 3 both targeting B, so request 1's timeout would incorrectly
  // fire and clear request 3's still-valid override).
  const requestGenerationRef = useRef<Map<string | number, number>>(new Map());

  // Ref to read optimisticUpdates without adding it as an effect dependency
  // (still needed by handleDragEnd below).
  const optimisticUpdatesRef = useRef(optimisticUpdates);
  optimisticUpdatesRef.current = optimisticUpdates;

  useOptimisticOverrideCleanup({
    items,
    getItemId,
    getItemStage,
    validStageIds,
    optimisticUpdates,
    setOptimisticUpdates,
    clearTimeoutsRef,
    inFlightByItemRef,
    requestGenerationRef,
  });

  // Compute items with optimistic updates applied
  const optimisticItems = useMemo(() => {
    if (optimisticUpdates.size === 0) {
      return items;
    }

    return items.map(item => {
      const newStage = optimisticUpdates.get(getItemId(item));
      if (
        newStage !== undefined &&
        (validStageIds === undefined || validStageIds.has(newStage))
      ) {
        return setItemStage(item, newStage);
      }
      return item;
    });
  }, [items, optimisticUpdates, getItemId, setItemStage, validStageIds]);

  // Handle drag start
  const handleDragStart = useCallback(
    (item: T) => {
      // Clear any pending clear-timeout for THIS item (if re-dragging it quickly)
      const itemId = getItemId(item);
      const existingTimeout = clearTimeoutsRef.current.get(itemId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        clearTimeoutsRef.current.delete(itemId);
      }

      setDragState({
        activeItem: item,
        originalStage: getItemStage(item),
        overStage: null,
      });
    },
    [getItemId, getItemStage]
  );

  // Handle drag over a column (just track for visual feedback).
  // Early-return when stage hasn't changed to avoid unnecessary state updates
  // during rapid onDragOver events (fires on every pixel of mouse movement).
  const handleDragOver = useCallback((stageId: string) => {
    setDragState(prev => {
      if (prev.overStage === stageId) {
        return prev;
      }
      return { ...prev, overStage: stageId };
    });
  }, []);

  // Handle drag cancel (escape or drop outside)
  const handleDragCancel = useCallback(() => {
    setDragState({
      activeItem: null,
      originalStage: null,
      overStage: null,
    });
    // Don't clear optimisticUpdates — other in-flight moves should stay
  }, []);

  // Handle drag end - apply optimistic update, call API, rollback on error
  const handleDragEnd = useCallback(
    async (targetStage: string | null) => {
      // Capture drag state before resetting
      const { activeItem, originalStage } = dragState;

      // No valid drop target or no change
      if (!activeItem || !targetStage || targetStage === originalStage) {
        // Reset drag state
        setDragState({
          activeItem: null,
          originalStage: null,
          overStage: null,
        });
        return;
      }

      const itemId = getItemId(activeItem);

      logger.debug('pipeline-dnd: optimistic override set', {
        itemId: String(itemId),
        originalStage,
        targetStage,
      });

      // Bump this item's generation before the awaited API call so the
      // timeout-install guard and expiry callback below can verify they still
      // own the override even when a later request targets the same stage.
      const currentGeneration =
        (requestGenerationRef.current.get(itemId) ?? 0) + 1;
      requestGenerationRef.current.set(itemId, currentGeneration);

      // Mark a request in flight for this item so generation-retirement sites
      // don't reset the counter out from under this or any other concurrent
      // request. Decremented in the finally block below.
      inFlightByItemRef.current.set(
        itemId,
        (inFlightByItemRef.current.get(itemId) ?? 0) + 1
      );

      // Apply optimistic update for THIS item (preserves other in-flight updates)
      setOptimisticUpdates(prev => {
        const next = new Map(prev);
        next.set(itemId, targetStage);
        return next;
      });

      // Reset drag state after optimistic update is set
      setDragState({
        activeItem: null,
        originalStage: null,
        overStage: null,
      });

      activeOperationsRef.current += 1;
      setIsUpdating(true);

      try {
        // Call the API
        await onStageChange(activeItem, targetStage);

        // No success refetch: the card is already in the target column via the
        // optimistic override, and the store mutation + WebSocket broadcast
        // (see useDealStageMove) keep `items` fresh, which drives the
        // reconciliation effect below. A post-drop refetch(list) + refetch(stages)
        // here just duplicates that work on every move. The reconciliation effect
        // clears this item's override when server data confirms the stage; the
        // safety timeout below is the backstop if confirmation never arrives.

        // Guard against a stale request: while this API call was in flight the
        // override may have been cleared or replaced — e.g. the removed-stage
        // effect retired it because its target column left the board, or the
        // user started a fresh drag of the same item. If the current override
        // no longer maps this item to THIS move's target, installing a safety
        // timeout would later clear whatever override is current, including a
        // subsequent drag's fresh one. Skip installation entirely in that case.
        if (
          optimisticUpdatesRef.current.get(itemId) !== targetStage ||
          requestGenerationRef.current.get(itemId) !== currentGeneration
        ) {
          logger.debug(
            'pipeline-dnd: skipping safety timeout — override superseded before confirmation',
            { itemId: String(itemId), targetStage }
          );
        } else {
          // Safety timeout: if the reconciliation effect hasn't cleared this
          // optimistic update within 10s (e.g. server never confirms), clear it
          // to avoid stale overrides. Normally, the reconciliation effect clears
          // it much sooner when fresh items arrive with the confirmed stage.
          const existingTimeout = clearTimeoutsRef.current.get(itemId);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
          }
          clearTimeoutsRef.current.set(
            itemId,
            setTimeout(() => {
              // Server never confirmed this move within the window (WS gap or
              // projection lag). Clearing the override may visibly snap the
              // card back to whatever stage `items` still reports — worth a
              // warn, because to the user it looks like the card teleported.
              logger.warn(
                'pipeline-dnd: optimistic override expired without server confirmation',
                { itemId: String(itemId), targetStage }
              );
              // Capture generation ownership before entering setState so the
              // check is consistent across re-runs of the updater (Strict Mode
              // may invoke updaters twice). If a newer request has bumped the
              // generation, leave the current override intact.
              const isCurrentOwner =
                requestGenerationRef.current.get(itemId) === currentGeneration;
              // Retire the generation entry only when this is still the latest
              // request AND no other request for the item is in flight. An older
              // move may still be running (its finally hasn't fired yet); if we
              // reset the counter now, a later drag would reuse a generation the
              // older in-flight request still holds, letting that stale request
              // clear the newer drag's override.
              if (isCurrentOwner && !inFlightByItemRef.current.get(itemId)) {
                requestGenerationRef.current.delete(itemId);
              }
              setOptimisticUpdates(prev => {
                // isCurrentOwner is the primary guard against same-stage false
                // matches; stage-equality is a secondary sanity check.
                if (prev.get(itemId) !== targetStage || !isCurrentOwner) {
                  return prev;
                }
                const next = new Map(prev);
                next.delete(itemId);
                return next;
              });
              clearTimeoutsRef.current.delete(itemId);
            }, 10_000)
          );
        }
      } catch (error) {
        // Rollback THIS item's optimistic update on error
        setOptimisticUpdates(prev => {
          const next = new Map(prev);
          next.delete(itemId);
          return next;
        });

        // Retire this move's generation entry so it doesn't linger after the
        // override is rolled back — but only when this is the latest request
        // (no newer drag bumped the generation) AND no other request for the
        // item is still in flight (this failing request is the only one, still
        // counted here before the finally decrement). Deleting otherwise would
        // strip the guard protecting another request's still-valid override.
        if (
          requestGenerationRef.current.get(itemId) === currentGeneration &&
          (inFlightByItemRef.current.get(itemId) ?? 0) <= 1
        ) {
          requestGenerationRef.current.delete(itemId);
        }

        // Refresh to sync with server state
        onRefresh();

        // Notify parent component of rollback
        onRollback?.();

        // Show error toast
        toast.error(getUserFriendlyEventError(error));

        logger.error(
          'pipeline-dnd: stage change failed — optimistic move rolled back',
          error instanceof Error ? error : new Error(String(error)),
          { itemId: String(itemId), originalStage, targetStage }
        );
      } finally {
        // Decrement this item's in-flight count. When it reaches zero the item
        // has no concurrent requests, so its generation entry can be retired IF
        // the item has also fully settled — no live override and no pending
        // safety timeout. With no in-flight request left, no stale request can
        // reuse the generation, so the entry is retired regardless of whether
        // this request's generation is still the current one — a newer move
        // that failed before this older one settled leaves the current
        // generation non-matching, and requiring equality here would leak it.
        // This is the backstop for paths that clear the override while a request
        // is in flight (e.g. the target stage left the board mid-move): those
        // sites skip generation retirement precisely because a request is in
        // flight, and rely on it happening here once the request settles.
        const remainingInFlight =
          (inFlightByItemRef.current.get(itemId) ?? 1) - 1;
        if (remainingInFlight > 0) {
          inFlightByItemRef.current.set(itemId, remainingInFlight);
        } else {
          inFlightByItemRef.current.delete(itemId);
          if (
            !optimisticUpdatesRef.current.has(itemId) &&
            !clearTimeoutsRef.current.has(itemId)
          ) {
            requestGenerationRef.current.delete(itemId);
          }
        }

        activeOperationsRef.current -= 1;
        if (activeOperationsRef.current === 0) {
          setIsUpdating(false);
        }
      }
    },
    [dragState, getItemId, onStageChange, onRefresh, onRollback]
  );

  return {
    dragState,
    optimisticItems,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    isUpdating,
  };
}
